'use client';

import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeftIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

type SupportMessage = {
  id: string;
  sender_type: 'customer' | 'admin' | 'system';
  message: string;
  created_at: string;
};

type Conversation = {
  id: string;
  status: 'waiting' | 'assigned' | 'active' | 'closed';
};

const CHAT_ID_KEY = 'dntech_human_chat_id';
const CHAT_NAME_KEY = 'dntech_human_chat_name';
const CHAT_PHONE_KEY = 'dntech_human_chat_phone';

export default function HumanSupportChat({ onBack }: { onBack: () => void }) {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  const loadMessages = async (conversationId: string) => {
    try {
      const response = await fetch(`/api/chat/messages?conversationId=${conversationId}`, {
        cache: 'no-store',
      });
      if (!response.ok) {
        if (response.status === 404) localStorage.removeItem(CHAT_ID_KEY);
        return;
      }
      const payload = await response.json();
      setMessages(Array.isArray(payload.messages) ? payload.messages : []);
      setConversation({ id: conversationId, status: payload.status || 'waiting' });
    } catch {
      // Polling failure is temporary; keep the current conversation on screen.
    }
  };

  useEffect(() => {
    const savedId = localStorage.getItem(CHAT_ID_KEY);
    const savedName = localStorage.getItem(CHAT_NAME_KEY) || '';
    const savedPhone = localStorage.getItem(CHAT_PHONE_KEY) || '';
    setName(savedName);
    setPhone(savedPhone);
    if (savedId) void loadMessages(savedId);
  }, []);

  useEffect(() => {
    if (!conversation?.id || conversation.status === 'closed') return;
    const timer = window.setInterval(() => void loadMessages(conversation.id), 3000);
    return () => window.clearInterval(timer);
  }, [conversation?.id, conversation?.status]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startConversation = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cleanName = name.trim();
    const cleanPhone = phone.replace(/\s/g, '');
    if (!cleanName) return setError('Vui lòng nhập họ tên.');
    if (!/^[0-9]{10,11}$/.test(cleanPhone)) {
      return setError('Số điện thoại phải có 10 hoặc 11 chữ số.');
    }

    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/chat/init', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: cleanName, phone: cleanPhone }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.id) {
        throw new Error(payload.error || 'Không thể tạo cuộc trò chuyện.');
      }
      localStorage.setItem(CHAT_ID_KEY, payload.id);
      localStorage.setItem(CHAT_NAME_KEY, cleanName);
      localStorage.setItem(CHAT_PHONE_KEY, cleanPhone);
      setName(cleanName);
      setPhone(cleanPhone);
      setConversation(payload);
      setMessages([]);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Không thể kết nối nhân viên.');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const content = input.trim();
    if (!content || !conversation || loading) return;

    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/chat/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: conversation.id,
          message: content,
          senderName: name,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Không thể gửi tin nhắn.');
      setInput('');
      await loadMessages(conversation.id);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Không thể gửi tin nhắn.');
    } finally {
      setLoading(false);
    }
  };

  const startNewConversation = () => {
    localStorage.removeItem(CHAT_ID_KEY);
    setConversation(null);
    setMessages([]);
    setInput('');
    setError('');
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-slate-50">
      <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 py-2.5">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-900"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Quay lại Chatbot AI
        </button>
        {conversation ? (
          <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold text-emerald-700">
            Đã tạo chat cho admin
          </span>
        ) : null}
      </div>

      {!conversation ? (
        <div className="flex flex-1 items-center overflow-y-auto px-5 py-6 sm:px-7">
          <form onSubmit={startConversation} className="w-full space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-extrabold text-slate-900">Chat trực tiếp với nhân viên</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">
                Nhập thông tin để tạo cuộc trò chuyện. Nhân viên sẽ nhận được chat trong trang quản trị.
              </p>
            </div>
            <label className="block text-xs font-bold uppercase text-slate-600">
              Họ tên
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={120}
                placeholder="Nhập tên của bạn"
                className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-normal normal-case outline-none focus:border-yellow-400 focus:ring-4 focus:ring-yellow-400/15"
              />
            </label>
            <label className="block text-xs font-bold uppercase text-slate-600">
              Số điện thoại
              <input
                value={phone}
                onChange={(event) => setPhone(event.target.value.replace(/[^0-9]/g, ''))}
                inputMode="numeric"
                maxLength={11}
                placeholder="Ví dụ: 0987123456"
                className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-normal normal-case outline-none focus:border-yellow-400 focus:ring-4 focus:ring-yellow-400/15"
              />
            </label>
            {error ? <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p> : null}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-yellow-400 px-4 py-3 text-sm font-extrabold text-slate-950 hover:bg-yellow-500 disabled:opacity-60"
            >
              {loading ? 'Đang tạo cuộc trò chuyện...' : 'Bắt đầu chat với nhân viên'}
            </button>
          </form>
        </div>
      ) : (
        <>
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4 sm:px-6">
            {messages.length === 0 ? (
              <div className="rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-slate-700">
                Cuộc trò chuyện đã xuất hiện trong trang quản trị. Hãy gửi tin nhắn đầu tiên cho nhân viên.
              </div>
            ) : null}
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[86%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
                    message.sender_type === 'customer'
                      ? 'rounded-br-md bg-yellow-400 text-slate-950'
                      : 'rounded-bl-md border border-slate-200 bg-white text-slate-700'
                  }`}
                >
                  {message.message}
                </div>
              </div>
            ))}
            {error ? <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p> : null}
            <div ref={endRef} />
          </div>

          {conversation.status === 'closed' ? (
            <div className="border-t border-slate-200 bg-white p-4 text-center">
              <p className="mb-2 text-xs text-slate-500">Cuộc trò chuyện đã kết thúc.</p>
              <button
                type="button"
                onClick={startNewConversation}
                className="rounded-lg bg-yellow-400 px-4 py-2 text-sm font-bold text-slate-950"
              >
                Tạo cuộc trò chuyện mới
              </button>
            </div>
          ) : (
            <form onSubmit={sendMessage} className="flex shrink-0 items-center gap-2 border-t border-slate-200 bg-white p-4">
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                maxLength={4000}
                placeholder="Nhập tin nhắn cho nhân viên..."
                className="min-w-0 flex-1 rounded-full border border-slate-300 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-yellow-400 focus:bg-white focus:ring-4 focus:ring-yellow-400/15"
              />
              <button
                type="submit"
                aria-label="Gửi tin nhắn cho nhân viên"
                disabled={!input.trim() || loading}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-slate-950 disabled:opacity-40"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}
